/*
 * YESOUL Frequently Bought Together DOM adapter
 * 2026-08-12
 *
 * 插件将图片与选择信息输出为两套列表。本适配器只重组展示节点，
 * 不修改 checkbox、variant select、价格节点或插件加购行为。
 */

(() => {
  // Match the app itself: merchants may use legacy insertion or theme app blocks.
  const WIDGET_SELECTOR = '.cbb-frequently-bought-container';
  const ROW_SELECTOR = '.cbb-frequently-bought-selector-list > li';
  const TILE_SELECTOR = '.cbb-frequently-bought-products > .cbb-frequently-bought-product';
  const RENDER_EVENT = 'codeblackbelt:frequently-bought-together:rendered';

  let observer;
  let scheduled = false;

  const directChild = (element, selector) => Array.from(element.children).find((child) => child.matches(selector));

  const rowLabel = (row) => {
    const label = row.querySelector('.cbb-frequently-bought-selector-label-name');
    return label ? label.textContent.replace(/\s+/g, ' ').trim() : 'Frequently bought product';
  };

  const buildMedia = (row, tile) => {
    row.querySelector(':scope > .ys-fbt-media')?.remove();

    const media = document.createElement('div');
    media.className = 'ys-fbt-media';

    const source = tile?.querySelector('.cbb-frequently-bought-product-image-link')
      || tile?.querySelector('.cbb-frequently-bought-product-image');

    if (source) {
      const clone = source.cloneNode(true);

      if (clone instanceof HTMLAnchorElement) {
        const destination = clone.getAttribute('href') || clone.dataset.href;

        if (destination) {
          clone.setAttribute('href', destination);
        }

        clone.setAttribute('aria-label', rowLabel(row));
      } else {
        clone.setAttribute('role', 'img');
        clone.setAttribute('aria-label', rowLabel(row));
      }

      media.append(clone);
    }

    row.prepend(media);
  };

  const buildContent = (row) => {
    let content = directChild(row, '.ys-fbt-content');

    if (!content) {
      content = document.createElement('div');
      content.className = 'ys-fbt-content';

      row.append(content);
    }

    const title = directChild(row, '.cbb-frequently-bought-selector-link')
      || Array.from(row.children).find((child) => child.querySelector?.('.cbb-frequently-bought-selector-label-name'));
    const variantSelect = directChild(row, '.cbb-recommendations-variant-select');

    if (title && title !== content) {
      content.append(title);
    }

    if (variantSelect) {
      content.append(variantSelect);
    }

    return content;
  };

  const buildPrice = (row) => {
    let price = directChild(row, '.ys-fbt-price');

    if (!price) {
      price = document.createElement('div');
      price.className = 'ys-fbt-price';

      row.append(price);
    }

    [
      '.cbb-frequently-bought-selector-label-compare-at-price',
      '.cbb-frequently-bought-selector-label-regular-price',
      '.cbb-frequently-bought-selector-label-sale-price'
    ].forEach((selector) => {
      const priceNode = directChild(row, selector);

      if (priceNode) {
        price.append(priceNode);
      }
    });

    return price;
  };

  const syncSelection = (row) => {
    const checkbox = row.querySelector('.cbb-frequently-bought-selector-input');
    const selected = Boolean(checkbox?.checked);

    row.classList.toggle('is-ys-selected', selected);
    row.classList.toggle('is-ys-unselected', !selected);
  };

  const enhanceWidget = (widget) => {
    const rows = Array.from(widget.querySelectorAll(ROW_SELECTOR));
    const tiles = Array.from(widget.querySelectorAll(TILE_SELECTOR));

    if (!rows.length) {
      return;
    }

    rows.forEach((row, index) => {
      row.classList.add('ys-fbt-row');
      buildMedia(row, tiles[index]);
      buildContent(row);
      buildPrice(row);

      const checkbox = row.querySelector('.cbb-frequently-bought-selector-input');
      if (checkbox) {
        row.append(checkbox);
      }

      syncSelection(row);
    });

    widget.classList.add('ys-fbt-ready');
  };

  const enhanceAll = () => {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(enhanceWidget);
  };

  const observe = () => {
    if (!document.body) {
      return;
    }

    observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;

        if (target?.closest(WIDGET_SELECTOR)) {
          return true;
        }

        return Array.from(mutation.addedNodes).some((node) => (
          node instanceof Element
          && (node.matches(WIDGET_SELECTOR) || node.querySelector(WIDGET_SELECTOR))
        ));
      });

      if (relevant) {
        scheduleEnhancement();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const flushEnhancement = () => {
    scheduled = false;
    observer?.disconnect();
    enhanceAll();
    observe();
  };

  function scheduleEnhancement() {
    if (scheduled) {
      return;
    }

    scheduled = true;
    window.requestAnimationFrame(flushEnhancement);
  }

  document.addEventListener('change', (event) => {
    const row = event.target.closest?.(`${WIDGET_SELECTOR} ${ROW_SELECTOR}`);

    if (!row) {
      return;
    }

    syncSelection(row);
    window.setTimeout(scheduleEnhancement, 100);
  });

  document.addEventListener(RENDER_EVENT, scheduleEnhancement);
  document.addEventListener('shopify:section:load', scheduleEnhancement);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      enhanceAll();
      observe();
    }, { once: true });
  } else {
    enhanceAll();
    observe();
  }
})();
